import { usePage, router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { AppLayout } from "@/components/layouts/AppLayout"
import { PageHeader } from "@/components/PageHeader"
import { StageChip } from "@/components/StageChip"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { FlashMessages } from "@/components/FlashMessages"
import { Link } from "@inertiajs/react"
import { Workflow, Beer, ArrowRightLeft, Truck, ChevronRight, Building2, MapPin } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import type { AppPageProps } from "@/types"
import type { AssetType, AssetTypeInfo } from "@/lib/assetTypes"

interface Stage {
  id: string
  name: string
  color: string
}

interface StageMetric extends Stage {
  count: number
}

interface ProductMetric {
  name: string
  count: number
}

interface Tag {
  id: number
  name: string
  color: string
}

interface RecentEvent {
  id: string
  event_type: "stage_change" | "location_change"
  created_at: string
  notes?: string | null
  user?: { id: number; name: string | null; email: string } | null
  asset_id: string
  asset_code: string
  asset_type?: string
  asset_product?: string | null
  asset_container?: string | null
  asset_customer?: string | null
  from_stage?: Stage | null
  to_stage?: Stage | null
  from_location?: { id: string; name: string } | null
  to_location?: { id: string; name: string } | null
  customer?: { id: string; name: string } | null
}

interface DashboardPageProps extends Omit<AppPageProps, "auth"> {
  auth: AppPageProps["auth"] | null
  metrics: {
    totalAssets: number
    byStage: StageMetric[]
    byProduct?: ProductMetric[]
  }
  assetType: AssetType
  assetTypes: AssetTypeInfo[]
  tags: Tag[]
  recentEvents: RecentEvent[]
  filters: {
    tag_id: string
  }
}

function greetingKey(d: Date) {
  const h = d.getHours()
  if (h < 12) return "goodMorning"
  if (h < 18) return "goodAfternoon"
  return "goodEvening"
}

function formatRelative(iso: string, t: (k: string, o?: Record<string, unknown>) => string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const diff = Math.max(0, now - then)
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return t("dashboard.activity.justNow", { defaultValue: "agora" })
  if (mins < 60) return t("dashboard.activity.minutesAgo", { defaultValue: "há {{n}} min", n: mins })
  const hours = Math.floor(mins / 60)
  if (hours < 24) return t("dashboard.activity.hoursAgo", { defaultValue: "há {{n}} h", n: hours })
  const days = Math.floor(hours / 24)
  return t("dashboard.activity.daysAgo", { defaultValue: "há {{n}} d", n: days })
}

export default function Dashboard() {
  const { t } = useTranslation()
  const {
    auth,
    flash,
    metrics,
    assetType,
    assetTypes,
    tags,
    recentEvents,
    filters,
  } = usePage<DashboardPageProps>().props

  const [hoverSeg, setHoverSeg] = useState<number | null>(null)

  if (!auth) return null

  const handleFilterChange = (key: string, value: string) => {
    const params: Record<string, string> = {
      asset_type: assetType,
      tag_id: filters.tag_id || "",
    }
    params[key] = value === "all" ? "" : value
    router.get(window.location.pathname, params, { preserveState: true, preserveScroll: true })
  }

  const activeSegments = metrics.byStage.filter((s: StageMetric) => s.count > 0)
  const total = activeSegments.reduce((sum: number, s: StageMetric) => sum + s.count, 0)
  let cum = 0
  const segments = activeSegments.map((s: StageMetric) => {
    const start = total > 0 ? cum / total : 0
    cum += s.count
    const end = total > 0 ? cum / total : 0
    return { ...s, start, end }
  })

  const arc = (startF: number, endF: number, r1: number, r2: number) => {
    const a1 = startF * 2 * Math.PI - Math.PI / 2
    const a2 = endF * 2 * Math.PI - Math.PI / 2
    const large = endF - startF > 0.5 ? 1 : 0
    const cx = 150
    const cy = 150
    const x1 = cx + r1 * Math.cos(a1), y1 = cy + r1 * Math.sin(a1)
    const x2 = cx + r1 * Math.cos(a2), y2 = cy + r1 * Math.sin(a2)
    const x3 = cx + r2 * Math.cos(a2), y3 = cy + r2 * Math.sin(a2)
    const x4 = cx + r2 * Math.cos(a1), y4 = cy + r2 * Math.sin(a1)
    return `M ${x1} ${y1} A ${r1} ${r1} 0 ${large} 1 ${x2} ${y2} L ${x3} ${y3} A ${r2} ${r2} 0 ${large} 0 ${x4} ${y4} Z`
  }

  const productData = metrics.byProduct ?? []
  const hasProductData = assetType === "Keg" && productData.length > 0
  const sortedProducts = [...productData].sort((a, b) => b.count - a.count)
  const maxBar = sortedProducts.reduce((m: number, b: ProductMetric) => Math.max(m, b.count), 1)
  const productTotal = sortedProducts.reduce((s, b) => s + b.count, 0)

  return (
    <AppLayout
      user={auth.user}
      accounts={auth.accounts}
      currentAccountId={auth.currentAccountId}
      role={auth.role}
    >
      <FlashMessages notice={flash.notice} alert={flash.alert} />

      <div className="space-y-6">
        <PageHeader
          eyebrow={t("dashboard.eyebrow", { defaultValue: "Visão geral" })}
          title={t(`dashboard.${greetingKey(new Date())}`, {
            defaultValue: "Olá, {{name}}.",
            name: auth.user.name || auth.user.email,
          })}
          subtitle={t("dashboard.subtitle", {
            defaultValue: "Acompanhe o que está acontecendo no seu inventário hoje.",
          })}
        />

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <label className="mono-col block">{t("inventory.filters.assetType")}</label>
            <Select value={assetType} onValueChange={(value) => handleFilterChange("asset_type", value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {assetTypes.map(({ type, key }: AssetTypeInfo) => (
                  <SelectItem key={type} value={type}>
                    {t(`assetTypes.${key}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {tags.length > 0 && (
            <div className="space-y-1.5">
              <label className="mono-col block">{t("inventory.filters.tag")}</label>
              <Select
                value={filters.tag_id || "all"}
                onValueChange={(value) => handleFilterChange("tag_id", value)}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder={t("dashboard.selectTag")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("dashboard.allTags")}</SelectItem>
                  {tags.map((tag: Tag) => (
                    <SelectItem key={tag.id} value={String(tag.id)}>
                      <div className="flex items-center gap-2">
                        <div className="h-3 w-3 rounded-full" style={{ backgroundColor: tag.color }} />
                        {tag.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="ml-auto font-mono text-[11px] uppercase text-muted-foreground tracking-[0.1em] self-end pb-2">
            {t("dashboard.updatedAt", {
              defaultValue: "Atualizado · {{time}}",
              time: new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
            })}
          </div>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-7 gap-3">
          <MetricCard
            label={t("dashboard.totalAssets")}
            value={metrics.totalAssets}
            color="var(--foreground)"
            href={inventoryHref(assetType, filters.tag_id)}
          />
          {metrics.byStage.map((stage: StageMetric) => (
            <MetricCard
              key={stage.id}
              label={stage.name}
              value={stage.count}
              color={stage.color}
              href={inventoryHref(assetType, filters.tag_id, stage.id)}
            />
          ))}
        </div>

        {/* Charts row */}
        <div className={`grid gap-4 ${hasProductData ? "md:grid-cols-2" : "md:grid-cols-1"}`}>
          {/* Donut — Assets by Stage */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-display tracking-tight">
                <IconTile icon={Workflow} />
                {t("dashboard.assetsByStage")}
              </CardTitle>
              <CardDescription>
                {t("dashboard.assetsByStageDescription", { defaultValue: "Distribuição atual" })}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {total === 0 ? (
                <div className="flex h-[260px] items-center justify-center text-muted-foreground">
                  {t("dashboard.noData")}
                </div>
              ) : (
                <div className="flex flex-col md:flex-row gap-5 items-center">
                  <svg width="260" height="260" viewBox="0 0 300 300">
                    {segments.map((seg: typeof segments[number], i: number) => (
                      <path
                        key={seg.id}
                        d={arc(seg.start, seg.end, 110, 70)}
                        fill={seg.color}
                        opacity={hoverSeg === null || hoverSeg === i ? 1 : 0.45}
                        onMouseEnter={() => setHoverSeg(i)}
                        onMouseLeave={() => setHoverSeg(null)}
                        style={{
                          transition: "opacity 160ms, transform 160ms",
                          transformOrigin: "150px 150px",
                          transform: hoverSeg === i ? "scale(1.03)" : "none",
                          cursor: "pointer",
                        }}
                      />
                    ))}
                    <text
                      x="150" y="145"
                      textAnchor="middle"
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 10,
                        fill: "var(--muted-foreground)",
                        letterSpacing: "0.18em",
                      }}
                    >
                      TOTAL
                    </text>
                    <text
                      x="150" y="178"
                      textAnchor="middle"
                      style={{
                        fontFamily: "var(--font-display)",
                        fontSize: 34,
                        fontWeight: 700,
                        fill: "var(--foreground)",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {hoverSeg !== null ? segments[hoverSeg].count : total}
                    </text>
                    {hoverSeg !== null && (
                      <text
                        x="150" y="200"
                        textAnchor="middle"
                        style={{
                          fontFamily: "var(--font-body)",
                          fontSize: 11,
                          fill: "var(--muted-foreground)",
                        }}
                      >
                        {segments[hoverSeg].name}
                      </text>
                    )}
                  </svg>

                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5 w-full">
                    {segments.map((seg: typeof segments[number], i: number) => (
                      <div
                        key={seg.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors"
                        style={{
                          background: hoverSeg === i ? "var(--secondary)" : "transparent",
                        }}
                        onMouseEnter={() => setHoverSeg(i)}
                        onMouseLeave={() => setHoverSeg(null)}
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-sm"
                          style={{ background: seg.color }}
                        />
                        <span className="flex-1 text-[12.5px] text-foreground truncate">{seg.name}</span>
                        <span className="font-mono text-xs font-semibold">{seg.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Custom bar chart — Kegs by Product */}
          {hasProductData && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 font-display tracking-tight">
                  <IconTile icon={Beer} />
                  {t("dashboard.kegsByProduct")}
                </CardTitle>
                <CardDescription>
                  {t("dashboard.kegsByProductDescription", {
                    defaultValue: "{{n}} produtos · {{total}} barris",
                    n: sortedProducts.length,
                    total: productTotal,
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {(() => {
                  const n = sortedProducts.length
                  const dense = n > 8
                  const veryDense = n > 14
                  const gap = veryDense ? "gap-1" : dense ? "gap-2" : "gap-5"
                  const labelClass = veryDense
                    ? "text-[10px] leading-[1.15]"
                    : dense
                    ? "text-[11px] leading-[1.2]"
                    : "text-[12px] leading-[1.25]"
                  // Reserve fixed label space so bars share a baseline regardless of name length
                  const labelHeight = veryDense ? 32 : dense ? 36 : 44
                  return (
                    <div className={`flex items-stretch ${gap} pt-6`}>
                      {sortedProducts.map((b: ProductMetric) => {
                        const h = Math.max(4, (b.count / maxBar) * 100)
                        return (
                          <div
                            key={b.name}
                            className="flex-1 min-w-0 flex flex-col items-center gap-2"
                          >
                            <div className="w-full flex items-end relative" style={{ height: 220 }}>
                              <div
                                className="absolute left-0 right-0 text-center font-mono text-[11px] font-semibold text-foreground tabular-nums transition-[bottom] duration-500 ease-out"
                                style={{ bottom: `calc(${h}% + 2px)` }}
                              >
                                {b.count}
                              </div>
                              <div
                                className="relative w-full rounded-t-md overflow-hidden"
                                style={{
                                  height: `${h}%`,
                                  background:
                                    "linear-gradient(to top, var(--accent), color-mix(in oklab, var(--accent) 80%, transparent))",
                                  transition: "height 500ms cubic-bezier(.22,1,.36,1)",
                                }}
                              >
                                <div
                                  className="absolute inset-0"
                                  style={{
                                    background:
                                      "repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 2px, transparent 2px 8px)",
                                  }}
                                />
                              </div>
                            </div>
                            <div
                              className={`text-muted-foreground text-center w-full overflow-hidden ${labelClass}`}
                              title={b.name}
                              style={{
                                height: labelHeight,
                                display: "-webkit-box",
                                WebkitLineClamp: 3,
                                WebkitBoxOrient: "vertical",
                                wordBreak: "break-word",
                                hyphens: "auto",
                              }}
                            >
                              {b.name}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )
                })()}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recent activity */}
        <RecentActivity events={recentEvents} t={t} />
      </div>
    </AppLayout>
  )
}

function inventoryHref(assetType: AssetType, tagId?: string, stageId?: string): string {
  const params = new URLSearchParams({ asset_type: assetType })
  if (tagId) params.set("tag_id", tagId)
  if (stageId) params.set("stage_id", stageId)
  return `/inventory?${params.toString()}`
}

function MetricCard({
  label,
  value,
  color,
  href,
}: {
  label: string
  value: number
  color: string
  href: string
}) {
  return (
    <Link href={href} className="block">
      <Card className="relative overflow-hidden transition-transform hover:-translate-y-0.5 hover:shadow-[0_8px_20px_-12px_rgba(0,0,0,0.25)] cursor-pointer rounded-md py-0">
        <span aria-hidden className="absolute left-0 top-0 bottom-0 w-[3px]" style={{ background: color }} />
        <div className="px-4 py-3.5">
          <p className="mono-col">{label}</p>
          <p className="font-display text-3xl font-bold mt-1 leading-none tracking-tight">{value}</p>
        </div>
      </Card>
    </Link>
  )
}

function IconTile({ icon: Icon }: { icon: LucideIcon }) {
  return (
    <span
      className="grid place-items-center w-7 h-7 rounded-md"
      style={{
        background: "color-mix(in oklab, var(--accent) 15%, transparent)",
        color: "var(--accent)",
      }}
    >
      <Icon className="w-4 h-4" />
    </span>
  )
}

const ACTIVITY_META = {
  stage_change: {
    shortKey: "dashboard.activity.shortStage",
    shortDefault: "Etapa",
    labelKey: "inventory.events.stage_change",
    icon: ArrowRightLeft,
    hue: "#7C4A7A",
  },
  location_change: {
    shortKey: "dashboard.activity.shortMovement",
    shortDefault: "Movimento",
    labelKey: "inventory.events.location_change",
    icon: Truck,
    hue: "#B8732A",
  },
} as const

type ActivityType = keyof typeof ACTIVITY_META

function RecentActivity({
  events,
  t,
}: {
  events: RecentEvent[]
  t: (k: string, o?: Record<string, unknown>) => string
}) {
  const [filter, setFilter] = useState<ActivityType | "all">("all")

  const counts: Record<ActivityType | "all", number> = {
    all: events.length,
    stage_change: events.filter((e) => e.event_type === "stage_change").length,
    location_change: events.filter((e) => e.event_type === "location_change").length,
  }

  const shown = filter === "all" ? events : events.filter((e) => e.event_type === filter)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 font-display tracking-tight">
              <IconTile icon={Workflow} />
              {t("dashboard.recentActivity", { defaultValue: "Atividade recente" })}
            </CardTitle>
            <CardDescription>
              {t("dashboard.recentActivityDescription", { defaultValue: "Últimos eventos do inventário" })}
            </CardDescription>
          </div>
          <Link
            href="/inventory"
            className="inline-flex items-center gap-1 text-[12.5px] font-medium"
            style={{ color: "var(--accent)" }}
          >
            {t("dashboard.viewAll", { defaultValue: "Ver tudo" })}
            <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filter chips */}
        <div
          className="flex gap-1.5 flex-wrap pb-3.5 mb-1.5"
          style={{ borderBottom: "1px dashed var(--border)" }}
        >
          <ActivityChip active={filter === "all"} onClick={() => setFilter("all")}>
            {t("dashboard.activity.all", { defaultValue: "Tudo" })} · {counts.all}
          </ActivityChip>
          {(Object.keys(ACTIVITY_META) as ActivityType[]).map((k) => {
            const meta = ACTIVITY_META[k]
            const Icon = meta.icon
            return (
              <ActivityChip
                key={k}
                active={filter === k}
                color={meta.hue}
                onClick={() => setFilter(k)}
              >
                <span
                  className="grid place-items-center w-[14px] h-[14px] rounded-full mr-1"
                  style={{ background: filter === k ? "rgba(255,255,255,0.22)" : `${meta.hue}22` }}
                >
                  <Icon
                    className="w-[9px] h-[9px]"
                    style={{ color: filter === k ? "#fff" : meta.hue }}
                  />
                </span>
                {t(meta.labelKey)} · {counts[k]}
              </ActivityChip>
            )
          })}
        </div>

        <div className="flex flex-col">
          {shown.length === 0 ? (
            <div className="py-8 text-center text-[13px] text-muted-foreground">
              {t("dashboard.activity.empty", { defaultValue: "Nenhum evento deste tipo." })}
            </div>
          ) : (
            shown.map((e, i) => (
              <ActivityRow
                key={e.id}
                event={e}
                isLast={i === shown.length - 1}
                t={t}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function ActivityChip({
  active,
  color,
  onClick,
  children,
}: {
  active?: boolean
  color?: string
  onClick: () => void
  children: React.ReactNode
}) {
  const accent = color || "var(--foreground)"
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center py-1 pr-2.5 pl-1.5 rounded-full text-[12px] font-medium transition-all"
      style={{
        background: active ? accent : "transparent",
        color: active ? "#fff" : "var(--foreground)",
        border: `1px solid ${active ? accent : "var(--border)"}`,
      }}
    >
      {children}
    </button>
  )
}

function ActivityRow({
  event,
  isLast,
  t,
}: {
  event: RecentEvent
  isLast: boolean
  t: (k: string, o?: Record<string, unknown>) => string
}) {
  const [hover, setHover] = useState(false)
  const meta = ACTIVITY_META[event.event_type]
  const Icon = meta.icon
  const hue = meta.hue
  const when = new Date(event.created_at)
  const whenClock = when.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
  const who = event.user ? (event.user.name || event.user.email) : t("inventory.events.system")
  const initials = who
    .split(" ")
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()
  const productLine = [event.asset_product, event.asset_container].filter(Boolean).join(" · ")
  const assetHref = `/inventory/${event.asset_id}${event.asset_type ? `?asset_type=${event.asset_type}` : ""}`

  return (
    <Link
      href={assetHref}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className="relative grid items-center gap-4 py-3.5 pl-2 pr-3 cursor-pointer transition-colors"
      style={{
        gridTemplateColumns: "44px minmax(140px, 170px) 1fr auto",
        borderBottom: isLast ? "none" : "1px solid var(--border-soft)",
        background: hover ? `${hue}14` : "transparent",
      }}
    >
      {/* Column 1: rail + circular glyph */}
      <div className="relative flex justify-center">
        <span
          aria-hidden
          className="absolute"
          style={{
            left: -8,
            top: -14,
            bottom: -14,
            width: 3,
            background: hue,
            opacity: hover ? 1 : 0.85,
            borderRadius: 2,
          }}
        />
        <div
          className="grid place-items-center w-[34px] h-[34px] rounded-full"
          style={{
            background: `${hue}20`,
            border: `1px solid ${hue}44`,
            color: hue,
          }}
        >
          <Icon className="w-[15px] h-[15px]" style={{ color: hue }} />
        </div>
      </div>

      {/* Column 2: asset identity */}
      <div className="min-w-0 flex flex-col gap-0.5">
        <div
          className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: hue }}
        >
          {t(meta.shortKey, { defaultValue: meta.shortDefault })}
        </div>
        <span className="font-mono text-[13px] font-semibold text-foreground tracking-wide truncate">
          {event.asset_code}
        </span>
        {productLine && (
          <div className="text-[12px] text-muted-foreground truncate">{productLine}</div>
        )}
      </div>

      {/* Column 3: type-specific detail */}
      <div className="min-w-0">
        {event.event_type === "stage_change" && (
          <StageChangeDetail event={event} customerFallback={event.asset_customer} />
        )}
        {event.event_type === "location_change" && (
          <MovementDetail event={event} hue={hue} t={t} />
        )}
      </div>

      {/* Column 4: who + when */}
      <div className="flex flex-col items-end gap-1 shrink-0">
        <div className="inline-flex items-center gap-1.5 text-[12.5px] text-foreground">
          <span
            className="grid place-items-center w-5 h-5 rounded-full font-mono text-[9px] font-bold tracking-wide"
            style={{ background: "var(--secondary)", color: "var(--foreground)" }}
          >
            {initials || "?"}
          </span>
          <span className="truncate max-w-[120px]">{who}</span>
        </div>
        <div className="font-mono text-[10.5px] text-muted-foreground tracking-wide">
          {formatRelative(event.created_at, t)} · {whenClock}
        </div>
      </div>
    </Link>
  )
}

function StageChangeDetail({
  event,
  customerFallback,
}: {
  event: RecentEvent
  customerFallback?: string | null
}) {
  const location = event.customer?.name || customerFallback
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        {event.from_stage && <StageChip stage={event.from_stage} />}
        {event.from_stage && event.to_stage && (
          <ChevronRight className="w-3 h-3 text-muted-foreground" />
        )}
        {event.to_stage && <StageChip stage={event.to_stage} />}
      </div>
      {location && (
        <div className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
          <MapPin className="w-3 h-3" />
          {location}
        </div>
      )}
    </div>
  )
}

function MovementDetail({
  event,
  hue,
  t,
}: {
  event: RecentEvent
  hue: string
  t: (k: string, o?: Record<string, unknown>) => string
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="inline-flex items-center gap-2 flex-wrap text-[13px] text-foreground">
        {event.from_location ? (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-[3px] rounded-md font-medium"
            style={{ background: "var(--secondary)" }}
          >
            <Building2 className="w-3 h-3 text-muted-foreground" />
            {event.from_location.name}
          </span>
        ) : (
          <span className="italic text-muted-foreground text-[12.5px]">
            {t("inventory.events.fromNoLocation", { defaultValue: "sem local anterior" })}
          </span>
        )}
        <span
          className="font-mono font-bold text-[13px] tracking-wide"
          style={{ color: hue }}
        >
          →
        </span>
        {event.to_location && (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-[3px] rounded-md font-semibold"
            style={{
              background: `${hue}18`,
              color: hue,
              border: `1px solid ${hue}44`,
            }}
          >
            <MapPin className="w-3 h-3" />
            {event.to_location.name}
          </span>
        )}
      </div>
      {event.notes && (
        <div className="font-mono text-[10.5px] uppercase tracking-[0.1em] text-muted-foreground font-medium truncate">
          {event.notes}
        </div>
      )}
    </div>
  )
}
